import React, { useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Search } from 'lucide-react';
import type { Activity, ActivityCategory } from '@/lib/types';

interface VirtualActivityListProps {
  categories: ActivityCategory[];
  selectedActivities: Set<string>;
  onActivityToggle: (activityId: string, checked: boolean) => void;
}

interface VirtualItem {
  type: 'category' | 'activity';
  data: ActivityCategory | Activity;
  categoryIndex?: number;
  activityIndex?: number;
}

/**
 * Virtualized activity list component for performance optimization
 * Renders only visible items to handle 800+ activities efficiently
 */
export default function VirtualActivityList({ 
  categories, 
  selectedActivities, 
  onActivityToggle 
}: VirtualActivityListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set([0])); // First category expanded by default
  const parentRef = useRef<HTMLDivElement>(null);

  // Filter and flatten data for virtualization
  const virtualItems = useMemo(() => {
    const filteredCategories = searchTerm.trim() 
      ? categories
          .map((category, categoryIndex) => ({
            ...category,
            categoryIndex,
            activities: category.activities.filter((activity) =>
              activity.name.toLowerCase().includes(searchTerm.toLowerCase())
            ),
          }))
          .filter((category) => category.activities.length > 0)
      : categories.map((category, categoryIndex) => ({ ...category, categoryIndex }));

    const items: VirtualItem[] = [];
    
    filteredCategories.forEach((category) => {
      // Add category header
      items.push({
        type: 'category',
        data: category,
        categoryIndex: category.categoryIndex,
      });

      // Add activities if category is expanded or search is active
      if (expandedCategories.has(category.categoryIndex) || searchTerm.trim()) {
        category.activities.forEach((activity, activityIndex) => {
          items.push({
            type: 'activity',
            data: activity,
            categoryIndex: category.categoryIndex,
            activityIndex,
          });
        });
      }
    });

    return items;
  }, [categories, searchTerm, expandedCategories]);

  // Virtual scrolling setup
  const rowVirtualizer = useVirtualizer({
    count: virtualItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => {
      const item = virtualItems[index];
      return item.type === 'category' ? 60 : 80; // Category headers smaller than activity items
    },
    overscan: 10, // Render extra items for smooth scrolling
  });

  const toggleCategory = (categoryIndex: number) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryIndex)) {
        newSet.delete(categoryIndex);
      } else {
        newSet.add(categoryIndex);
      }
      return newSet;
    });
  };

  const renderVirtualItem = (virtualItem: VirtualItem, style: React.CSSProperties) => {
    if (virtualItem.type === 'category') {
      const category = virtualItem.data as ActivityCategory;
      const isExpanded = expandedCategories.has(virtualItem.categoryIndex!);
      
      return (
        <div
          style={style}
          className="flex items-center justify-between p-4 bg-slate-700/50 border-b border-slate-600 cursor-pointer hover:bg-slate-700/70 transition-colors"
          onClick={() => toggleCategory(virtualItem.categoryIndex!)}
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleCategory(virtualItem.categoryIndex!);
            }
          }}
        >
          <h3 className="text-lg font-semibold text-gray-200">
            {category.name} ({category.activities.length})
          </h3>
          <div className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      );
    } else {
      const activity = virtualItem.data as Activity;
      
      return (
        <div
          style={style}
          className="flex items-center space-x-3 p-4 hover:bg-slate-700/30 transition-colors border-b border-slate-800/50"
        >
          <Checkbox
            id={activity.id}
            checked={selectedActivities.has(activity.id)}
            onCheckedChange={(checked) => onActivityToggle(activity.id, !!checked)}
            className="border-slate-500 data-[state=checked]:bg-brand-teal data-[state=checked]:border-brand-teal"
          />
          <Label 
            htmlFor={activity.id} 
            className="text-gray-300 text-sm cursor-pointer flex-grow"
          >
            <div className="font-medium">{activity.name}</div>
            <div className="text-xs text-gray-400 mt-1">
              {activity.frequency} - Approx. {activity.timeSaved} hrs/year
            </div>
          </Label>
        </div>
      );
    }
  };

  return (
    <div className="pr-4 md:pr-8 h-full flex flex-col">
      {/* Search Header - Sticky */}
      <div className="sticky top-28 bg-brand-dark/80 backdrop-blur-sm py-4 z-10 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" aria-hidden="true" />
          <Input
            type="search"
            placeholder="Search activities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-gray-400 focus:ring-brand-teal"
            aria-label="Search activities"
            aria-describedby="search-results-count"
          />
        </div>
        {searchTerm && (
          <div id="search-results-count" className="mt-2 text-sm text-gray-400" role="status" aria-live="polite">
            Found {virtualItems.filter(item => item.type === 'activity').length} activities
          </div>
        )}
      </div>

      {/* Virtual Scrolling Container */}
      <div 
        ref={parentRef} 
        className="flex-1 overflow-auto mt-4"
        style={{ height: '600px' }} // Fixed height for virtualization
        role="region"
        aria-label="Activity list"
      >
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const item = virtualItems[virtualRow.index];
            return (
              <div
                key={virtualRow.key}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {renderVirtualItem(item, {})}
              </div>
            );
          })}
        </div>
      </div>

      {/* No Results Message */}
      {virtualItems.length === 0 && (
        <div className="text-center text-gray-400 mt-8 flex-shrink-0">
          No activities found matching your search.
        </div>
      )}
    </div>
  );
}
